import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiTrophy, FiCalendar, FiClock, FiInfinity, FiStar } = FiIcons;

function LeaderboardScreen({ onBack }) {
  const [scores, setScores] = useState([]);
  const [filter, setFilter] = useState('allTime');
  const [lastSavedScore, setLastSavedScore] = useState(null);

  const filters = [
    { id: 'today', label: 'Today', icon: FiCalendar },
    { id: 'week', label: 'This Week', icon: FiCalendar },
    { id: 'allTime', label: 'All Time', icon: FiStar }
  ];

  useEffect(() => {
    loadScores();
    loadLastSavedScore();
  }, [filter]);

  const loadScores = () => {
    const allScores = JSON.parse(localStorage.getItem('colorMixScores') || '[]');
    const now = new Date();
    
    let filteredScores = allScores;
    
    if (filter === 'today') {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      filteredScores = allScores.filter(score => {
        const scoreDate = new Date(score.timestamp);
        return scoreDate >= today;
      });
    } else if (filter === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filteredScores = allScores.filter(score => {
        const scoreDate = new Date(score.timestamp);
        return scoreDate >= weekAgo;
      });
    }
    
    setScores(filteredScores.slice(0, 50));
  };

  const loadLastSavedScore = () => {
    const lastScore = JSON.parse(localStorage.getItem('lastSavedScore') || 'null');
    setLastSavedScore(lastScore);
  };

  const getRankEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'hard': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const isCurrentPlayerScore = (score) => {
    return lastSavedScore && 
           score.playerName === lastSavedScore.playerName &&
           score.timestamp === lastSavedScore.timestamp;
  };

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onBack}
            className="bg-white/20 backdrop-blur-sm p-3 rounded-xl"
          >
            <SafeIcon icon={FiArrowLeft} className="text-xl text-white" />
          </motion.button>
          
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <SafeIcon icon={FiTrophy} className="text-yellow-400" />
              Leaderboard
            </h1>
          </div>
          
          <div className="w-12" />
        </div>

        {/* Filter Tabs */}
        <div className="flex bg-white/10 backdrop-blur-sm rounded-2xl p-2 mb-6">
          {filters.map((filterOption) => (
            <motion.button
              key={filterOption.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setFilter(filterOption.id)}
              className={`flex-1 py-2 px-3 rounded-xl transition-all duration-200 ${
                filter === filterOption.id
                  ? 'bg-white text-purple-900 font-bold'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              <div className="flex items-center justify-center gap-1">
                <SafeIcon icon={filterOption.icon} className="text-sm" />
                <span className="text-xs">{filterOption.label}</span>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Scores List */}
        <div className="space-y-3">
          {scores.length === 0 ? (
            <div className="text-center py-12">
              <SafeIcon icon={FiTrophy} className="text-6xl text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No scores yet</p>
              <p className="text-gray-500 text-sm">Be the first to set a record!</p>
            </div>
          ) : (
            scores.map((score, index) => (
              <motion.div
                key={`${score.playerName}-${score.timestamp}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className={`bg-white/10 backdrop-blur-sm rounded-2xl p-4 border-2 ${
                  isCurrentPlayerScore(score) 
                    ? 'border-yellow-400/50 bg-yellow-400/10' 
                    : 'border-transparent'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl font-bold">
                      {getRankEmoji(index + 1)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-bold">
                          {score.playerName}
                        </h3>
                        {isCurrentPlayerScore(score) && (
                          <span className="bg-yellow-400/20 text-yellow-400 text-xs px-2 py-1 rounded-full">
                            You
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span className={getDifficultyColor(score.difficulty)}>
                          {score.difficulty?.charAt(0).toUpperCase() + score.difficulty?.slice(1)}
                        </span>
                        <span>•</span>
                        <SafeIcon 
                          icon={score.gameMode === 'timed' ? FiClock : FiInfinity} 
                          className="text-xs" 
                        />
                        <span>{score.gameMode}</span>
                        <span>•</span>
                        <span>{formatDate(score.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-xl font-bold text-white">
                      {score.score.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-400">
                      L{score.level} • S{score.streak}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Stats Summary */}
        {scores.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mt-8 bg-white/10 backdrop-blur-sm rounded-2xl p-4"
          >
            <h3 className="text-white font-bold mb-3 text-center">
              {filter === 'today' ? "Today's" : filter === 'week' ? "This Week's" : 'All Time'} Stats
            </h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-gray-400 text-sm">Total Scores</p>
                <p className="text-white font-bold">{scores.length}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">High Score</p>
                <p className="text-white font-bold">
                  {scores.length > 0 ? scores[0].score.toLocaleString() : '0'}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Avg Score</p>
                <p className="text-white font-bold">
                  {scores.length > 0 
                    ? Math.round(scores.reduce((sum, s) => sum + s.score, 0) / scores.length).toLocaleString()
                    : '0'
                  }
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default LeaderboardScreen;